import {
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  createLeague,
  deleteLeague,
  getLeagueById,
  getLeagueByInviteCode,
  getLeagueDuelPicks,
  getLeagueGwSummary,
  getLeagueMembers,
  getMyLeagues,
  getPublicLeagues,
  joinByCode,
  joinLeague,
  leaveLeague,
  regenerateInviteCode,
  removeMember,
  updateLeague,
} from '@/api/leagues';
import { useAuth } from '@/hooks/auth';
import type {
  CreateLeagueBody,
  JoinByCodeBody,
  League,
  LeagueDuelPick,
  LeagueDuelPicksTab,
  LeagueGwSummary,
  LeagueMember,
  MyLeagueItem,
  Pagination,
  PublicLeagueItem,
  UpdateLeagueBody,
} from '@/types/leagues';

export type PublicLeaguesPage = {
  leagues: PublicLeagueItem[];
  pagination: Pagination;
};

export type JoinLeagueMutationResult = { league: League; already_member: boolean };

export type CreateLeagueMutationResult = Awaited<ReturnType<typeof createLeague>>;

export const LEAGUES_ME_KEY = ['leagues', 'me'] as const;

export const LEAGUES_PUBLIC_KEY = (search: string | undefined) => ['leagues', 'public', { search }] as const;

export const LEAGUE_KEY = (id: string) => ['leagues', id] as const;

export const LEAGUE_MEMBERS_KEY = (id: string) => ['leagues', id, 'members'] as const;

export const LEAGUE_MEMBERS_STANDINGS_KEY = (id: string, pageSize: number) =>
  ['leagues', id, 'members', 'standings', { pageSize }] as const;

export const LEAGUE_BY_CODE_KEY = (code: string) => ['leagues', 'by-code', code] as const;

export const LEAGUE_GW_SUMMARY_INFINITE_KEY = (id: string, gameweekId: string | undefined, pageSize: number) =>
  ['leagues', id, 'gw-summary', { gameweekId, pageSize }] as const;

export const LEAGUE_DUEL_PICKS_KEY = (id: string, slug: string) => ['leagues', id, 'duels', slug, 'picks'] as const;

function matchesLeaguesByCodePrefix(key: unknown): boolean {
  return Array.isArray(key) && key.length >= 2 && key[0] === 'leagues' && key[1] === 'by-code';
}

export function useMyLeagues(): UseQueryResult<MyLeagueItem[], Error> {
  const isAuthenticated = useAuth(s => s.isAuthenticated);
  return useQuery({
    queryKey: LEAGUES_ME_KEY,
    queryFn: getMyLeagues,
    staleTime: 30_000,
    enabled: isAuthenticated,
  });
}

export function useLeague(id: string | undefined, opts?: { enabled?: boolean }): UseQueryResult<League, Error> {
  return useQuery({
    queryKey: LEAGUE_KEY(id ?? ''),
    queryFn: () => getLeagueById(id!),
    staleTime: 30_000,
    enabled: !!id && (opts?.enabled ?? true),
  });
}

export function useLeagueMembers(
  id: string | undefined,
  opts?: { enabled?: boolean; fetchForPublicGuests?: boolean },
): UseQueryResult<LeagueMember[], Error> {
  const isAuthenticated = useAuth(s => s.isAuthenticated);
  const canFetchSansAuth = opts?.fetchForPublicGuests === true;
  return useQuery({
    queryKey: LEAGUE_MEMBERS_KEY(id ?? ''),
    queryFn: async () => {
      const res = await getLeagueMembers(id!, { limit: 100, offset: 0 });
      return res.members;
    },
    staleTime: 30_000,
    enabled: !!id && (isAuthenticated || canFetchSansAuth) && (opts?.enabled ?? true),
  });
}

export function useInfiniteLeagueMembersStandings(
  leagueId: string | undefined,
  opts?: { enabled?: boolean; fetchForPublicGuests?: boolean; pageSize?: number },
): UseInfiniteQueryResult<InfiniteData<{ members: LeagueMember[]; pagination: Pagination }>, Error> {
  const pageSize = opts?.pageSize ?? 30;
  const isAuthenticated = useAuth(s => s.isAuthenticated);
  const canFetchSansAuth = opts?.fetchForPublicGuests === true;
  return useInfiniteQuery({
    queryKey: LEAGUE_MEMBERS_STANDINGS_KEY(leagueId ?? '', pageSize),
    queryFn: ({ pageParam }) => getLeagueMembers(leagueId!, { limit: pageSize, offset: pageParam }),
    initialPageParam: 0,
    staleTime: 30_000,
    enabled: !!leagueId && (isAuthenticated || canFetchSansAuth) && (opts?.enabled ?? true),
    getNextPageParam: lastPage => {
      const next = lastPage.pagination.offset + lastPage.members.length;
      return next < lastPage.pagination.total_count ? next : undefined;
    },
  });
}

export function usePublicLeagues(options: {
  search?: string;
  pageSize?: number;
}): UseInfiniteQueryResult<InfiniteData<PublicLeaguesPage>, Error> {
  const { search, pageSize = 20 } = options;
  return useInfiniteQuery({
    queryKey: LEAGUES_PUBLIC_KEY(search),
    queryFn: ({ pageParam }) =>
      getPublicLeagues({
        search,
        limit: pageSize,
        offset: pageParam,
      }),
    initialPageParam: 0,
    staleTime: 30_000,
    getNextPageParam: lastPage => {
      const nextOffset = lastPage.pagination.offset + lastPage.leagues.length;
      return nextOffset < lastPage.pagination.total_count ? nextOffset : undefined;
    },
  });
}

export function useLeagueByCode(code: string | undefined, opts?: { enabled?: boolean }): UseQueryResult<League, Error> {
  return useQuery({
    queryKey: LEAGUE_BY_CODE_KEY(code ?? ''),
    queryFn: () => getLeagueByInviteCode(code!),
    staleTime: 30_000,
    enabled: !!code && code.length === 6 && (opts?.enabled ?? true),
    retry: false,
  });
}

export function useLeagueGwSummary(
  params: { leagueId: string | undefined; gameweekId?: string; pageSize?: number },
  opts?: { enabled?: boolean },
): UseInfiniteQueryResult<InfiniteData<LeagueGwSummary, number>, Error> {
  const isAuthenticated = useAuth(s => s.isAuthenticated);
  const { leagueId, gameweekId, pageSize = 24 } = params;
  return useInfiniteQuery({
    queryKey: LEAGUE_GW_SUMMARY_INFINITE_KEY(leagueId ?? '', gameweekId, pageSize),
    queryFn: ({ pageParam }) =>
      getLeagueGwSummary(leagueId!, {
        gameweek_id: gameweekId,
        limit: pageSize,
        offset: pageParam,
      }),
    initialPageParam: 0,
    staleTime: 60_000,
    enabled: !!leagueId && isAuthenticated && (opts?.enabled ?? true),
    getNextPageParam: (lastPage, _allPages, lastPageParam): number | undefined => {
      const pag = lastPage.duels_pagination;
      if (!pag?.has_more) return undefined;
      return lastPageParam + pageSize;
    },
  });
}

const LEAGUE_DUEL_PICKS_PAGE_SIZE = 20;

export function useInfiniteLeagueDuelPicksTab(
  params: { leagueId: string | undefined; duelSlug: string | undefined; pick: LeagueDuelPicksTab },
  opts?: { enabled?: boolean; pageSize?: number },
): {
  picks: LeagueDuelPick[];
  totalCount: number;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  error: Error | null;
} {
  const pageSize = opts?.pageSize ?? LEAGUE_DUEL_PICKS_PAGE_SIZE;
  const isAuthenticated = useAuth(s => s.isAuthenticated);
  const q = useInfiniteQuery({
    queryKey: [...LEAGUE_DUEL_PICKS_KEY(params.leagueId ?? '', params.duelSlug ?? ''), params.pick, pageSize] as const,
    queryFn: ({ pageParam }) =>
      getLeagueDuelPicks(params.leagueId!, params.duelSlug!, {
        pick: params.pick,
        limit: pageSize,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: lastPage => {
      const { offset, limit, total_count } = lastPage.pagination;
      const next = offset + limit;
      return next < total_count ? next : undefined;
    },
    enabled: !!params.leagueId && !!params.duelSlug && isAuthenticated && (opts?.enabled ?? true),
    staleTime: 30_000,
  });

  const picks = q.data?.pages.flatMap(p => p.picks) ?? [];
  const totalCount = q.data?.pages[0]?.pagination.total_count ?? 0;

  return {
    picks,
    totalCount,
    isLoading: q.isLoading,
    isFetchingNextPage: q.isFetchingNextPage,
    hasNextPage: q.hasNextPage ?? false,
    fetchNextPage: q.fetchNextPage,
    error: q.error,
  };
}

export function useCreateLeague(): UseMutationResult<CreateLeagueMutationResult, Error, CreateLeagueBody> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateLeagueBody) => createLeague(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LEAGUES_ME_KEY });
      void queryClient.invalidateQueries({ queryKey: ['leagues', 'public'] });
    },
  });
}

export function useUpdateLeague(leagueId: string): UseMutationResult<League, Error, UpdateLeagueBody> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdateLeagueBody) => updateLeague(leagueId, body),
    onSuccess: (league: League) => {
      void queryClient.invalidateQueries({ queryKey: LEAGUE_KEY(league.id) });
      void queryClient.invalidateQueries({ queryKey: LEAGUES_ME_KEY });
      void queryClient.invalidateQueries({ queryKey: ['leagues', 'public'] });
    },
  });
}

export function useDeleteLeague(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteLeague(id),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: LEAGUES_ME_KEY });
      void queryClient.invalidateQueries({ queryKey: ['leagues', 'public'] });
      queryClient.removeQueries({ queryKey: LEAGUE_KEY(id) });
    },
  });
}

export function useJoinLeague(): UseMutationResult<JoinLeagueMutationResult, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => joinLeague(id),
    onSuccess: data => {
      const id = data.league.id;
      void queryClient.invalidateQueries({ queryKey: LEAGUES_ME_KEY });
      void queryClient.invalidateQueries({ queryKey: ['leagues', 'public'] });
      void queryClient.invalidateQueries({ queryKey: LEAGUE_KEY(id) });
      void queryClient.invalidateQueries({ queryKey: ['leagues', id, 'members'] });
    },
  });
}

export function useJoinByCode(): UseMutationResult<JoinLeagueMutationResult, Error, JoinByCodeBody> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: JoinByCodeBody) => joinByCode(body),
    onSuccess: data => {
      const id = data.league.id;
      void queryClient.invalidateQueries({ queryKey: LEAGUES_ME_KEY });
      void queryClient.invalidateQueries({ queryKey: ['leagues', 'public'] });
      void queryClient.invalidateQueries({ queryKey: LEAGUE_KEY(id) });
      void queryClient.invalidateQueries({ queryKey: ['leagues', id, 'members'] });
    },
  });
}

export function useLeaveLeague(leagueId: string): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => leaveLeague(leagueId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LEAGUES_ME_KEY });
      void queryClient.invalidateQueries({ queryKey: ['leagues', 'public'] });
      void queryClient.invalidateQueries({ queryKey: LEAGUE_KEY(leagueId) });
    },
  });
}

export function useRemoveMember(leagueId: string): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (memberUserId: string) => removeMember(leagueId, memberUserId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'members'] });
      void queryClient.invalidateQueries({ queryKey: LEAGUE_KEY(leagueId) });
    },
  });
}

export function useRegenerateInviteCode(leagueId: string): UseMutationResult<string, Error, void> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => regenerateInviteCode(leagueId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LEAGUE_KEY(leagueId) });
      queryClient.removeQueries({ predicate: q => matchesLeaguesByCodePrefix(q.queryKey) });
    },
  });
}
